/**
 * Purpose: default fields for the Import geometry dialog.
 *
 * Input:  dropped filename / MIME type.
 * Output: extra instructions, system prompt, provider, and model.
 * Consumers: Import geometry dialog.
 */

import {
  getGeometryImportCadKind,
  getGeometryImportPromptSourceForm,
  getGeometryImportSourceKind,
} from '@shared/helpers/design/geometry-import-capabilities.helpers';
import { buildPdfGeometryPrompt } from '@shared/helpers/pdf-geometry-prompt.helpers';
import {
  CLAUDE_ALLOWED_MODELS,
  GEMINI_TEXT_ALLOWED_MODELS,
  GROK_ALLOWED_MODELS,
  OPEN_AI_ALLOWED_MODELS,
  type ClaudeAllowedModels,
  type GeminiTextAllowedModels,
  type GrokAllowedModels,
  type OpenAiAllowedModels,
} from '@shared/models/ai/ai-models.model';
import { AI_MODEL_USE_CASES } from '@shared/models/ai/ai-model-use-cases.model';
import {
  resolveOpenAiReasoningEffort,
  type OpenAiReasoningEffort,
} from '@shared/models/ai/open-ai-reasoning.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import type {
  PdfGeometryImportAi,
  PdfGeometryImportProvider,
} from '@shared/models/design/pdf-geometry-import.model';

/** Fields submitted from the Import geometry dialog, excluding the file. */
export type GeometryImportDialogValue = {
  extraInstructions: string;
  systemPrompt: string;
  ai: PdfGeometryImportAi;
};

/** Payload the Import geometry dialog passes to `onImport`. */
export type GeometryImportRequest = GeometryImportDialogValue & {
  file: File;
};

export type GeometryImportDialogDefaults = GeometryImportDialogValue & {
  geminiModel: GeminiTextAllowedModels;
  openAiModel: OpenAiAllowedModels;
  claudeModel: ClaudeAllowedModels;
  grokModel: GrokAllowedModels;
};

/** Default OpenAI reasoning effort for Import geometry with AI. */
export const DEFAULT_GEOMETRY_IMPORT_REASONING_EFFORT: OpenAiReasoningEffort =
  'low';

type OpenAiGeometryImportAi = Extract<
  PdfGeometryImportAi,
  { provider: 'openai' }
>;

/**
 * OpenAI selection for the Import geometry dialog.
 * Attaches `reasoningEffort` when the model accepts it as input; keeps
 * {@link previousEffort} when it is still valid for {@link model}.
 * {@link previousEffort} is untyped so Select values can pass through
 * {@link resolveOpenAiReasoningEffort}.
 */
export const buildOpenAiGeometryImportAi = (
  model: OpenAiAllowedModels,
  previousEffort?: unknown,
): OpenAiGeometryImportAi => {
  const reasoningEffort = resolveOpenAiReasoningEffort(model, previousEffort);
  if (reasoningEffort === undefined) return { provider: 'openai', model };
  return { provider: 'openai', model, reasoningEffort };
};

/**
 * Allowed models for the Import geometry dialog's Model select.
 */
export const geometryImportModelsForProvider = (
  provider: PdfGeometryImportProvider,
): readonly string[] => {
  switch (provider) {
    case 'openai':
      return OPEN_AI_ALLOWED_MODELS;
    case 'gemini':
      return GEMINI_TEXT_ALLOWED_MODELS;
    case 'claude':
      return CLAUDE_ALLOWED_MODELS;
    case 'grok':
      return GROK_ALLOWED_MODELS;
    default:
      return assertUnreachable(provider);
  }
};

/**
 * Default AI selection when the admin switches provider in the dialog.
 */
export const buildGeometryImportAiForProvider = (
  provider: PdfGeometryImportProvider,
  defaults: Pick<
    GeometryImportDialogDefaults,
    'geminiModel' | 'openAiModel' | 'claudeModel' | 'grokModel'
  >,
): PdfGeometryImportAi => {
  switch (provider) {
    case 'openai':
      return buildOpenAiGeometryImportAi(
        defaults.openAiModel,
        DEFAULT_GEOMETRY_IMPORT_REASONING_EFFORT,
      );
    case 'gemini':
      return { provider: 'gemini', model: defaults.geminiModel };
    case 'claude':
      return { provider: 'claude', model: defaults.claudeModel };
    case 'grok':
      return { provider: 'grok', model: defaults.grokModel };
    default:
      return assertUnreachable(provider);
  }
};

/**
 * Default Import geometry dialog fields for a dropped file.
 * IFC/DXF get CAD source-text rules; PDF gets attachment drawing rules.
 */
export const buildGeometryImportDialogDefaults = (input: {
  filename: string;
  mimeType?: string;
}): GeometryImportDialogDefaults => {
  const geminiModel = AI_MODEL_USE_CASES.pdfGeometryImportGemini.defaultModel;
  const openAiModel = AI_MODEL_USE_CASES.pdfGeometryImportOpenAi.defaultModel;
  const claudeModel = AI_MODEL_USE_CASES.pdfGeometryImportClaude.defaultModel;
  const grokModel = AI_MODEL_USE_CASES.pdfGeometryImportGrok.defaultModel;
  const sourceKind = getGeometryImportSourceKind({
    filename: input.filename,
    mimeType: input.mimeType,
  });
  return {
    extraInstructions: '',
    systemPrompt: buildPdfGeometryPrompt({
      arity: 'single',
      sourceForm: getGeometryImportPromptSourceForm(sourceKind),
      cadKind: getGeometryImportCadKind(input.filename),
    }),
    ai: buildOpenAiGeometryImportAi(
      openAiModel,
      DEFAULT_GEOMETRY_IMPORT_REASONING_EFFORT,
    ),
    geminiModel,
    openAiModel,
    claudeModel,
    grokModel,
  };
};
